import {Injectable} from '@angular/core';
import {disableNetwork, enableNetwork, Firestore} from "@angular/fire/firestore";

/**
 * Keeps the Firestore real-time connection alive on flaky/idle networks (e.g. an OBS/Caspar
 * browser source on LTE). When no snapshot has arrived for longer than the idle threshold the
 * underlying WebChannel may be silently dropped by carrier-grade NAT ("half-open" connection),
 * so push updates (control on/off) stop arriving. This watchdog forces a reconnect
 * (disableNetwork -> enableNetwork) after an idle period and on the browser 'online' event.
 *
 * Consumers must call notifyActivity() on every received snapshot so the idle timer only fires
 * during genuine inactivity (during active play the steady manualData stream keeps it warm).
 */
@Injectable({
  providedIn: 'root'
})
export class FirestoreKeepaliveService {

  private lastActivityAt = 0;
  private idleThresholdMs = 45000;
  private timerId: any = null;
  private started = false;
  private reconnecting = false;
  private readonly onOnline = () => this.forceReconnect();

  constructor(private readonly firestore: Firestore) {
  }

  notifyActivity(): void {
    this.lastActivityAt = Date.now();
  }

  start(idleThresholdMs = 45000, checkIntervalMs = 15000): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.idleThresholdMs = idleThresholdMs;
    this.lastActivityAt = Date.now();
    this.timerId = setInterval(() => this.check(), checkIntervalMs);
    window.addEventListener('online', this.onOnline);
  }

  stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    window.removeEventListener('online', this.onOnline);
  }

  private check(): void {
    if (this.reconnecting) {
      return;
    }
    if (Date.now() - this.lastActivityAt > this.idleThresholdMs) {
      this.forceReconnect();
    }
  }

  private async forceReconnect(): Promise<void> {
    if (this.reconnecting) {
      return;
    }
    this.reconnecting = true;
    try {
      await disableNetwork(this.firestore);
      await enableNetwork(this.firestore);
      console.log('[FirestoreKeepalive] forced reconnect after idle/offline');
    } catch (err) {
      console.error('[FirestoreKeepalive] reconnect failed', err);
    } finally {
      this.lastActivityAt = Date.now();
      this.reconnecting = false;
    }
  }
}
